(() => {
  "use strict";

  const SELECTOR_TIMELINE_ROWS = "[data-timeline-rows]";
  const SELECTOR_TIMELINE_ROW = "[data-timeline-row]";
  const SELECTOR_TIMELINE_ROW_EDITOR = "[data-timeline-row-editor]";
  const SELECTOR_TIMELINE_BUTTON = "[data-timeline-button]";
  const SELECTOR_BLOCK_SCROLL = "[data-block-scroll]";
  const CLASS_IS_SELECTED = "is-selected";
  const MOBILE_BREAKPOINT = 768;

  function isMobile() {
    return window.innerWidth < MOBILE_BREAKPOINT;
  }

  function throttle(fn, ms) {
    let last = 0;
    return function(...args) {
      const now = Date.now();
      if (now - last >= ms) {
        last = now;
        fn.apply(this, args);
      }
    };
  }

  if (!customElements.get("timeline-component")) {
    customElements.define("timeline-component", class extends HTMLElement {
      constructor() {
        super();
        this.rows = this.querySelectorAll(SELECTOR_TIMELINE_ROW);

        if (this.rows.length < 2) return;

        this.rowsWidth = 0;
        this.rowsHeight = [];
        this.holderTop = this.getBoundingClientRect().top + window.scrollY;
        this.holderHeight = this.offsetHeight;
        this.buttons = this.querySelectorAll(SELECTOR_TIMELINE_BUTTON);
        this.rowsHolder = this.querySelector(SELECTOR_TIMELINE_ROWS);
        this.requestAnimation = null;
        this.isScrollEnabled = false;

        this.scrollEvent = throttle(() => this.onScroll(), 50);
        this.resizeEvent = throttle(() => this.resizeEvents(), 100);
        this.rowsHolderScrollEvent = throttle(() => this.onRowsHolderScroll(), 50);
      }

      connectedCallback() {
        if (!this.rowsHolder) return;

        this.calculateRowsDimensions();
        this.requestAnimation = requestAnimationFrame(() => this.calculatePosition());

        if (!isMobile() && !this.isScrollEnabled) {
          this.isScrollEnabled = true;
          window.addEventListener("scroll", this.scrollEvent);
        }

        this.rowsHolder.addEventListener("scroll", this.rowsHolderScrollEvent);
        window.addEventListener("resize", this.resizeEvent);

        if (this.buttons.length) {
          this.buttons.forEach((button, index) => {
            button.addEventListener("click", (event) => {
              event.preventDefault();
              const row = event.currentTarget.closest(SELECTOR_TIMELINE_ROW);

              if (row) {
                if (isMobile()) {
                  const padding = 16;
                  const leftPosition = row.offsetLeft - padding;
                  this.rowsHolder.scrollTo({
                    left: leftPosition,
                    behavior: "smooth"
                  });
                } else {
                  const maxHeight = Math.max(...this.rowsHeight);
                  const rowHeight = this.holderHeight / this.rows.length;
                  const holderTopPosition = this.getBoundingClientRect().top;
                  const scrollToPosition = (index > 0 && maxHeight < rowHeight ?
                    rowHeight * index + holderTopPosition :
                    holderTopPosition) + 1;

                  window.scrollTo({
                    top: window.scrollY + scrollToPosition,
                    behavior: "smooth"
                  });
                }
              }
            });
          });
        }
      }

      onScroll() {
        if (!this.requestAnimation) {
          this.requestAnimation = requestAnimationFrame(() => this.calculatePosition());
        }
      }

      onRowsHolderScroll() {
        if (!this.requestAnimation) {
          this.requestAnimation = requestAnimationFrame(() => this.calculatePosition(this.rowsHolder));
        }
      }

      resizeEvents() {
        this.holderTop = this.getBoundingClientRect().top + window.scrollY;
        this.holderHeight = this.offsetHeight;
        this.calculateRowsDimensions();
        this.requestAnimation = requestAnimationFrame(() => this.calculatePosition());

        const isDesktop = !isMobile();

        if (isDesktop && !this.isScrollEnabled) {
          this.isScrollEnabled = true;
          window.addEventListener("scroll", this.scrollEvent);
        } else if (!isDesktop && this.isScrollEnabled) {
          this.isScrollEnabled = false;
          window.removeEventListener("scroll", this.scrollEvent);
        }
      }

      removeAnimationFrame() {
        if (this.requestAnimation) {
          cancelAnimationFrame(this.requestAnimation);
          this.requestAnimation = null;
        }
      }

      calculateRowsDimensions() {
        if (!this.rows.length) return;

        this.rowsHeight = [];
        this.rowsWidth = 0;
        let totalHeight = 0;

        this.rows.forEach((row, index) => {
          this.rowsHeight.push(row.offsetHeight);
          this.rowsWidth += row.offsetWidth + parseInt(getComputedStyle(row).marginRight || 0);

          if (window.Shopify && window.Shopify.designMode) {
            const rowEditors = this.querySelectorAll(SELECTOR_TIMELINE_ROW_EDITOR);
            if (rowEditors.length) {
              totalHeight += row.offsetHeight;
              if (rowEditors[index]) {
                rowEditors[index].style.setProperty("--row-height-min", `${row.offsetHeight}px`);
              }
              if (rowEditors[index + 1]) {
                rowEditors[index + 1].style.setProperty("--row-top-mobile", `${totalHeight}px`);
              }
            }
          }
        });
      }

      calculatePosition(target = null) {
        this.removeAnimationFrame();

        this.holderTop = this.getBoundingClientRect().top + window.scrollY;
        const isDesktop = !isMobile();
        const rowHeight = this.holderHeight / this.rows.length;
        let currentTop = this.holderTop;
        const windowHeight = window.innerHeight;
        const scrolled = window.scrollY + 1.5 * windowHeight;
        let percentComplete = 0;

        if (!isDesktop && target) {
          const scrollLeft = target.scrollLeft - 16;
          const holderWidth = target.offsetWidth;
          percentComplete = (scrollLeft / (this.rowsWidth - holderWidth)) * 100;
          this.style.setProperty("--percent-mobile", `${percentComplete}%`);
        }

        if (isDesktop && (scrolled < this.holderTop || scrolled > this.holderTop + this.holderHeight + windowHeight)) {
          return;
        }

        this.rows.forEach((row, index) => {
          if (index > 0) {
            const rowPercent = (row.offsetLeft / this.rowsWidth) * 100;
            let isVisible = percentComplete > rowPercent;

            if (isDesktop) {
              isVisible = currentTop + rowHeight < scrolled;

              if (currentTop - rowHeight < scrolled) {
                const previousRow = row.previousElementSibling;
                if (previousRow) {
                  previousRow.style.setProperty(
                    "--percent-desktop",
                    ((scrolled - currentTop) / rowHeight) * 100 + "%"
                  );
                }
              }
            }

            row.classList.toggle(CLASS_IS_SELECTED, isVisible);
          }

          currentTop += rowHeight;
        });
      }

      disconnectedCallback() {
        if (this.isScrollEnabled) {
          this.isScrollEnabled = false;
          window.removeEventListener("scroll", this.scrollEvent);
        }

        if (this.rowsHolder) {
          this.rowsHolder.removeEventListener("scroll", this.rowsHolderScrollEvent);
        }
        window.removeEventListener("resize", this.resizeEvent);
      }

      onBlockSelect(event) {
        const scrollContainer = this.querySelector(SELECTOR_BLOCK_SCROLL);
        if (scrollContainer) {
          const selectedElement = event.srcElement;
          const selectedIndex = [...selectedElement.parentElement.children].indexOf(selectedElement);
          const targetRow = this.rows[selectedIndex];
          if (targetRow) {
            scrollContainer.scrollTo({
              top: 0,
              left: targetRow.offsetLeft,
              behavior: "smooth"
            });
          }
        }
      }
    });
  }
})();
